import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import {
  ChevronRight,
  ExternalLink,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import {
  ALL_GRAMMAR_TOPICS,
  GRAMMAR_GROUPS,
  GRAMMAR_LEVELS,
  groupLevels,
  type GrammarGroupId,
  type GrammarLevel,
} from '@org/grammar-content';
import {
  GrammarLevelPill,
  levelColors,
} from '../../../components/grammar-level-pill';

// -- Feminist color palette (local copy — same convention as journey.tsx / mural.tsx) --
const P = {
  maxRed: '#D41920',
  sizzle: '#E84B5A',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  black: '#1A1018',
  white: '#FFFFFF',
  cream: '#FFF8F5',
  blush: '#FDF0ED',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightText: '#8A6F69',
  lightBorder: '#F0DAD5',
  shadow: 'rgba(180, 80, 80, 0.12)',
  shadowDeep: 'rgba(120, 40, 40, 0.18)',
};

const oswald = "'Oswald', sans-serif";
const dmSerif = "'DM Serif Text', serif";

// Chapter colours: the same six poster combos the journey wall cycles
// through (journey.tsx POSTER_STYLES), one per group, in order.
const GROUP_LOOK: Record<
  GrammarGroupId,
  { bg: string; text: string; border?: string }
> = {
  foundations: { bg: P.maxRed, text: P.white },
  everyday: { bg: P.baby, text: P.maxRed },
  modals: { bg: P.black, text: P.white },
  advancedTenses: { bg: P.charm, text: P.darkText },
  connecting: { bg: P.sizzle, text: P.white },
  fluency: { bg: P.white, text: P.maxRed, border: P.lightBorder },
};

// The source document the native section was ported from — kept as a
// reference/print option at the bottom of the page.
const GOOGLE_DOC_URL =
  'https://docs.google.com/document/d/1Wkzuij0SyLiz8H7OztjCwv9wCwml-YLou6W9mi5h81k/preview';

// The school's CEFR checklist the topic levels were taken from (same link as
// the student hub's "Checklist de Níveis" resource).
const CHECKLIST_URL =
  'https://english2empower.notion.site/p/Checklist-de-N-veis-English2Empower-ef0ca6e07cf34e33a0c672e11040c71f';

const TABS_WIDTH = 160;

const GRAMMAR_AID_STYLES = `
.ga-topic { transition: background .12s ease, color .12s ease; }
.ga-topic:nth-child(even) { background: ${P.blush}; }
.ga-topic:hover { background: ${P.baby}; }
.ga-topic:hover .ga-topic-title { color: ${P.maxRed}; }
.ga-topic:hover .ga-topic-arrow { opacity: 1; transform: translateX(0); }
.ga-topic-arrow { opacity: 0; transform: translateX(-4px); transition: opacity .12s ease, transform .12s ease; }
.ga-topic:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.ga-search:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 1px; }
.ga-chip { cursor: pointer; transition: background .12s ease, color .12s ease, border-color .12s ease, box-shadow .12s ease; }
.ga-chip:hover:not(:disabled) { box-shadow: 0 3px 12px ${P.shadow}; }
.ga-chip:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.ga-chip:disabled { cursor: not-allowed; opacity: .45; }
.ga-clear:hover { color: ${P.maxRed}; }
.ga-doclink:hover { text-decoration: underline; }
.ga-notebook { display: grid; grid-template-columns: ${TABS_WIDTH}px minmax(0, 1fr); gap: 22px; align-items: start; }
.ga-tabs { display: grid; gap: 7px; position: sticky; }
.ga-tab { cursor: pointer; text-align: left; border: none; transition: transform .15s ease, box-shadow .15s ease, opacity .15s ease; }
.ga-tab:hover:not(:disabled) { transform: translateX(4px); }
.ga-tab.is-current { transform: translateX(8px); box-shadow: 6px 8px 18px ${P.shadowDeep}; }
.ga-tab:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.ga-tab:disabled { cursor: default; opacity: .4; }
.ga-section { scroll-margin-top: var(--ga-scroll-margin, 140px); }
.ga-toolbar { position: sticky; top: 0; z-index: 5; }
@media (prefers-reduced-motion: reduce) {
  .ga-topic, .ga-chip, .ga-topic-arrow, .ga-tab { transition: none; }
}
@media (max-width: 720px) {
  .ga-topic-arrow { display: none; }
  /* Phone: the filter card is tall, let it scroll away; the chapter tabs
     become a horizontal strip above the chapters. */
  .ga-toolbar { position: static; }
  .ga-notebook { grid-template-columns: minmax(0, 1fr); gap: 14px; }
  .ga-tabs { position: static !important; display: flex; overflow-x: auto; gap: 8px; padding: 4px 2px 10px; margin: 0 -4px; scrollbar-width: thin; }
  .ga-tab { flex: 0 0 auto; border-radius: 6px !important; }
  .ga-tab.is-current { transform: none; }
  .ga-tab:hover:not(:disabled) { transform: none; }
  .ga-section { scroll-margin-top: 12px; }
}
`;

export const Route = createFileRoute('/_app/grammar-aid/')({
  component: GrammarAidIndex,
});

type LevelFilter = GrammarLevel | 'all';

function GrammarAidIndex() {
  const { t } = useTranslation('p2e');
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState<LevelFilter>('all');
  const [currentGroup, setCurrentGroup] =
    useState<GrammarGroupId>('foundations');
  const [toolbarHeight, setToolbarHeight] = useState(130);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<
    Partial<Record<GrammarGroupId, HTMLElement | null>>
  >({});

  const normalizedQuery = query.trim().toLowerCase();

  // Topics that match the search box alone (level counts are computed from
  // this set, so each chip tells how many results it would show).
  const searched = useMemo(
    () =>
      normalizedQuery
        ? ALL_GRAMMAR_TOPICS.filter((topic) =>
            topic.title.toLowerCase().includes(normalizedQuery)
          )
        : ALL_GRAMMAR_TOPICS,
    [normalizedQuery]
  );

  const countByLevel = useMemo(() => {
    const counts: Record<GrammarLevel, number> = {
      A1: 0,
      A2: 0,
      B1: 0,
      B2: 0,
      C1: 0,
    };
    for (const topic of searched) counts[topic.level] += 1;
    return counts;
  }, [searched]);

  // Every group, with only the topics that survive search + level filter
  // (groups with none keep an empty list so their tab can show "0").
  const filteredGroups = useMemo(() => {
    const visible = new Set(
      searched
        .filter((topic) => level === 'all' || topic.level === level)
        .map((topic) => topic.slug)
    );
    return GRAMMAR_GROUPS.map((group) => ({
      ...group,
      topics: group.topics.filter((topic) => visible.has(topic.slug)),
    }));
  }, [searched, level]);

  const resultCount = filteredGroups.reduce(
    (sum, group) => sum + group.topics.length,
    0
  );
  const isFiltering = level !== 'all' || normalizedQuery.length > 0;

  // Flat numbering 1..N across groups, so numbers survive filtering.
  const numberBySlug = useMemo(() => {
    const map = new Map<string, number>();
    ALL_GRAMMAR_TOPICS.forEach((topic, index) =>
      map.set(topic.slug, index + 1)
    );
    return map;
  }, []);

  // The sticky toolbar's real height drives where sections stop when a tab
  // scrolls them into view and where the tabs column sticks.
  useEffect(() => {
    const node = toolbarRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() =>
      setToolbarHeight(node.offsetHeight)
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Scroll spy: the tab of the first chapter in view is the "pulled out" one.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const visible = new Set<GrammarGroupId>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).dataset
            .group as GrammarGroupId;
          if (entry.isIntersecting) visible.add(id);
          else visible.delete(id);
        }
        const first = GRAMMAR_GROUPS.find((group) => visible.has(group.id));
        if (first) setCurrentGroup(first.id);
      },
      { rootMargin: `-${toolbarHeight + 8}px 0px -45% 0px`, threshold: 0 }
    );
    for (const group of filteredGroups) {
      const node = sectionRefs.current[group.id];
      if (node && group.topics.length > 0) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [filteredGroups, toolbarHeight]);

  const jumpToGroup = (id: GrammarGroupId) => {
    const node = sectionRefs.current[id];
    if (!node) return;
    setCurrentGroup(id);
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const chipBase: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    padding: '7px 12px 7px 10px',
    borderRadius: 999,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: P.lightBorder,
    background: P.white,
    color: P.darkText,
    fontFamily: oswald,
    fontWeight: 600,
    fontSize: 12,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    lineHeight: 1,
  };

  const clearFilters = () => {
    setQuery('');
    setLevel('all');
  };

  const scrollMargin = toolbarHeight + 16;

  return (
    <div style={{ minHeight: '100vh', background: P.cream, borderRadius: 12 }}>
      <style dangerouslySetInnerHTML={{ __html: GRAMMAR_AID_STYLES }} />

      <div
        style={
          {
            maxWidth: 940,
            margin: '0 auto',
            padding: '36px 20px 56px',
            '--ga-scroll-margin': `${scrollMargin}px`,
          } as CSSProperties
        }
      >
        <header style={{ marginBottom: 22 }}>
          <p
            style={{
              fontFamily: oswald,
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: P.maxRed,
              margin: 0,
            }}
          >
            {t('grammarAid.eyebrow')}
          </p>
          <h1
            style={{
              fontFamily: oswald,
              fontSize: 34,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: P.darkText,
              margin: '4px 0 6px',
              lineHeight: 1.05,
            }}
          >
            {t('grammarAid.title')}
          </h1>
          <p
            style={{
              fontFamily: dmSerif,
              fontStyle: 'italic',
              fontSize: 17,
              color: P.midText,
              margin: 0,
            }}
          >
            {t('grammarAid.tagline')}
          </p>
        </header>

        {/* Search + level filters: sticky so they stay at hand while
            scrolling the 83 topics. */}
        <div
          ref={toolbarRef}
          className="ga-toolbar"
          style={{
            background: P.cream,
            padding: '8px 0 14px',
            marginBottom: 6,
          }}
        >
          <div
            style={{
              background: P.white,
              border: `1px solid ${P.lightBorder}`,
              borderRadius: 10,
              boxShadow: `0 6px 20px ${P.shadow}`,
              padding: '14px 16px 12px',
              display: 'grid',
              gap: 12,
            }}
          >
            <div style={{ position: 'relative' }}>
              <Search
                size={16}
                color={P.lightText}
                style={{
                  position: 'absolute',
                  left: 13,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
                aria-hidden
              />
              <input
                className="ga-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('grammarAid.searchPlaceholder')}
                aria-label={t('grammarAid.searchPlaceholder')}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '11px 14px 11px 38px',
                  background: P.cream,
                  border: `1px solid ${P.lightBorder}`,
                  borderRadius: 999,
                  fontSize: 14.5,
                  color: P.darkText,
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div
              role="group"
              aria-label={t('grammarAid.levelsFilterLabel')}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: oswald,
                  fontWeight: 600,
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: P.lightText,
                  marginRight: 2,
                }}
              >
                <SlidersHorizontal size={13} aria-hidden />
                {t('grammarAid.levelsFilterLabel')}
              </span>

              <button
                type="button"
                className="ga-chip"
                aria-pressed={level === 'all'}
                onClick={() => setLevel('all')}
                style={{
                  ...chipBase,
                  ...(level === 'all'
                    ? {
                        background: P.darkText,
                        color: P.white,
                        borderColor: P.darkText,
                      }
                    : null),
                }}
              >
                {t('grammarAid.allLevels')}
                <span style={{ fontWeight: 500, opacity: 0.7 }}>
                  {searched.length}
                </span>
              </button>

              {GRAMMAR_LEVELS.map((code) => {
                const active = level === code;
                const colors = levelColors(code);
                const count = countByLevel[code];
                return (
                  <button
                    key={code}
                    type="button"
                    className="ga-chip"
                    aria-pressed={active}
                    disabled={count === 0 && !active}
                    onClick={() => setLevel(active ? 'all' : code)}
                    style={{
                      ...chipBase,
                      ...(active
                        ? {
                            background: colors.background,
                            color: colors.color,
                            borderColor: colors.border,
                            boxShadow: `0 3px 12px ${P.shadow}`,
                          }
                        : null),
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 5px',
                        borderRadius: 3,
                        background: active
                          ? 'rgba(255,255,255,0.28)'
                          : colors.background,
                        color: active ? 'inherit' : colors.color,
                        fontSize: 11,
                        letterSpacing: '0.08em',
                      }}
                    >
                      {code}
                    </span>
                    {t(`grammarAid.levels.${code}`)}
                    <span style={{ fontWeight: 500, opacity: 0.7 }}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Results line */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '6px 12px',
            margin: '0 4px 18px',
            fontSize: 13,
            color: P.lightText,
          }}
        >
          <span>
            <strong style={{ color: P.midText, fontWeight: 600 }}>
              {t('grammarAid.resultsCount', { count: resultCount })}
            </strong>
            {level !== 'all' && (
              <>
                {' · '}
                {t('grammarAid.resultsForLevel', {
                  level: `${level} ${t(`grammarAid.levels.${level}`)}`,
                })}
              </>
            )}
            {normalizedQuery && (
              <>
                {' · '}
                {t('grammarAid.resultsForQuery', { query: query.trim() })}
              </>
            )}
          </span>
          {isFiltering && (
            <button
              type="button"
              className="ga-clear"
              onClick={clearFilters}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: oswald,
                fontWeight: 600,
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: P.warmGray,
              }}
            >
              <X size={12} aria-hidden />
              {t('grammarAid.clearFilters')}
            </button>
          )}
        </div>

        {resultCount === 0 ? (
          <div
            style={{
              background: P.white,
              border: `1px solid ${P.lightBorder}`,
              borderRadius: 8,
              boxShadow: `6px 10px 22px ${P.shadow}`,
              padding: '34px 20px',
              textAlign: 'center',
              display: 'grid',
              gap: 10,
              justifyItems: 'center',
            }}
          >
            <p style={{ color: P.midText, fontSize: 15, margin: 0 }}>
              {normalizedQuery && level === 'all'
                ? t('grammarAid.noResults', { query: query.trim() })
                : t('grammarAid.noResultsFiltered')}
            </p>
            <button
              type="button"
              onClick={clearFilters}
              style={{
                fontFamily: oswald,
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: P.white,
                background: P.maxRed,
                border: 'none',
                borderRadius: 4,
                padding: '10px 18px',
                cursor: 'pointer',
              }}
            >
              {t('grammarAid.clearFilters')}
            </button>
          </div>
        ) : (
          <div className="ga-notebook">
            {/* Chapter tabs: binder dividers that follow the scroll and jump
                to a chapter; the chapter in view sits pulled out. */}
            <nav
              className="ga-tabs"
              aria-label={t('grammarAid.chaptersLabel')}
              style={{ top: scrollMargin }}
            >
              {filteredGroups.map((group, index) => {
                const look = GROUP_LOOK[group.id];
                const full = GRAMMAR_GROUPS[index];
                const isCurrent =
                  currentGroup === group.id && group.topics.length > 0;
                return (
                  <button
                    key={group.id}
                    type="button"
                    className={`ga-tab${isCurrent ? ' is-current' : ''}`}
                    aria-current={isCurrent ? 'true' : undefined}
                    disabled={group.topics.length === 0}
                    onClick={() => jumpToGroup(group.id)}
                    style={{
                      background: look.bg,
                      color: look.text,
                      boxShadow: look.border
                        ? `inset 0 0 0 1px ${look.border}, 3px 4px 12px ${P.shadow}`
                        : `3px 4px 12px ${P.shadow}`,
                      borderRadius: '0 8px 8px 0',
                      padding: '10px 12px 9px 14px',
                      fontFamily: oswald,
                      fontWeight: 600,
                      fontSize: 11.5,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      lineHeight: 1.2,
                    }}
                  >
                    <span style={{ opacity: 0.7, marginRight: 6 }}>
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {t(`grammarAid.groups.${group.id}`)}
                    <span
                      style={{
                        display: 'block',
                        fontFamily: 'inherit',
                        fontWeight: 500,
                        fontSize: 10.5,
                        letterSpacing: '0.06em',
                        textTransform: 'none',
                        opacity: 0.85,
                        marginTop: 3,
                      }}
                    >
                      {group.topics.length === full.topics.length
                        ? t('grammarAid.topicsCount', {
                            count: group.topics.length,
                          })
                        : `${group.topics.length} / ${full.topics.length}`}
                      {' · '}
                      {groupLevels(full).join(' ')}
                    </span>
                  </button>
                );
              })}
            </nav>

            <div style={{ display: 'grid', gap: 26, minWidth: 0 }}>
              {filteredGroups.map((group, index) => {
                if (group.topics.length === 0) return null;
                const look = GROUP_LOOK[group.id];
                const full = GRAMMAR_GROUPS[index];
                return (
                  <section
                    key={group.id}
                    id={`grammar-group-${group.id}`}
                    data-group={group.id}
                    ref={(node) => {
                      sectionRefs.current[group.id] = node;
                    }}
                    className="ga-section"
                    aria-label={t(`grammarAid.groups.${group.id}`)}
                    style={{
                      background: P.white,
                      border: `1px solid ${P.lightBorder}`,
                      borderRadius: 8,
                      boxShadow: `6px 10px 22px ${P.shadow}`,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Chapter band in the chapter's poster colour */}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: '6px 14px',
                        padding: '14px 18px',
                        background: look.bg,
                        color: look.text,
                        borderBottom: look.border
                          ? `1px solid ${look.border}`
                          : 'none',
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          fontFamily: oswald,
                          fontWeight: 700,
                          fontSize: 32,
                          lineHeight: 0.9,
                          opacity: 0.55,
                        }}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                        <h2
                          style={{
                            fontFamily: oswald,
                            fontWeight: 700,
                            fontSize: 20,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            margin: 0,
                            lineHeight: 1.05,
                          }}
                        >
                          {t(`grammarAid.groups.${group.id}`)}
                        </h2>
                        <p
                          style={{
                            fontFamily: dmSerif,
                            fontStyle: 'italic',
                            fontSize: 13.5,
                            margin: '3px 0 0',
                            opacity: 0.9,
                          }}
                        >
                          {t(`grammarAid.groupTaglines.${group.id}`)}
                        </p>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginLeft: 'auto',
                        }}
                      >
                        <span
                          style={{
                            fontFamily: oswald,
                            fontSize: 11,
                            letterSpacing: '0.12em',
                            opacity: 0.85,
                          }}
                        >
                          {group.topics.length === full.topics.length
                            ? t('grammarAid.topicsCount', {
                                count: group.topics.length,
                              })
                            : `${group.topics.length} / ${full.topics.length}`}
                        </span>
                        <span style={{ display: 'inline-flex', gap: 4 }}>
                          {groupLevels(full).map((code) => (
                            <GrammarLevelPill key={code} level={code} />
                          ))}
                        </span>
                      </div>
                    </div>

                    {/* Single column: the numbering reads top-to-bottom, per the founder. */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        padding: '10px 10px 8px',
                      }}
                    >
                      {group.topics.map((topic) => (
                        <Link
                          key={topic.slug}
                          to="/grammar-aid/$topicSlug"
                          params={{ topicSlug: topic.slug }}
                          className="ga-topic"
                          style={{
                            display: 'flex',
                            gap: 12,
                            alignItems: 'center',
                            padding: '9px 10px',
                            borderRadius: 5,
                            textDecoration: 'none',
                          }}
                        >
                          <span
                            style={{
                              fontFamily: oswald,
                              fontWeight: 600,
                              fontSize: 12,
                              color: P.maxRed,
                              opacity: 0.7,
                              minWidth: 22,
                            }}
                          >
                            {String(numberBySlug.get(topic.slug)).padStart(
                              2,
                              '0'
                            )}
                          </span>
                          <span
                            className="ga-topic-title"
                            style={{
                              fontFamily: dmSerif,
                              fontSize: 16,
                              color: P.darkText,
                              lineHeight: 1.3,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {topic.title}
                          </span>
                          <GrammarLevelPill level={topic.level} />
                          <ChevronRight
                            className="ga-topic-arrow"
                            size={16}
                            color={P.maxRed}
                            aria-hidden
                          />
                        </Link>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        )}

        <footer
          style={{
            marginTop: 34,
            display: 'grid',
            gap: 8,
            justifyItems: 'center',
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: 12.5, color: P.lightText, margin: 0 }}>
            {t('grammarAid.levelsHint')}{' '}
            <a
              className="ga-doclink"
              href={CHECKLIST_URL}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                color: P.maxRed,
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              {t('grammarAid.levelsLink')}
              <ExternalLink size={11} aria-hidden />
            </a>
          </p>
          <a
            className="ga-doclink"
            href={GOOGLE_DOC_URL}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: P.lightText,
              textDecoration: 'none',
            }}
          >
            {t('grammarAid.docLink')}
            <ExternalLink size={12} aria-hidden />
          </a>
        </footer>
      </div>
    </div>
  );
}
